import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { AdminAreaFiltersList } from "@/react/components/shared/MemberFilters/DynamicFilters/AllFiltersList/AdminAreaFiltersList";
import { useSmOrMdOrLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import botBuilderCanvasDark from "@circle-assets/images/bot-builder-canvas-dark.png";
import botBuilderCanvas from "@circle-assets/images/bot-builder-canvas.png";
import type { AgentProp } from "@circle-react/components/SettingsApp/CommunityBot/Agents";
import { usePunditUserContext } from "@circle-react/contexts";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useThemeContext } from "@circle-react/providers";
import { Icon } from "@circle-react-shared/Icon";
import type {
  BaseFilter,
  ProfileFieldBaseFilter,
} from "@circle-react-shared/MemberFilters/types";
import { TiptapEditor } from "@circle-react-shared/uikit/TipTap";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";
import { ChatPreview } from "../ChatPreview";
import { CollapsedSidebar } from "../CollapsedSidebar";
import { Header } from "../Header";
import { DynamicFilters } from "./DynamicFilters";
import { TouchpointsSection } from "./TouchpointsSection";
import { usePlatformMemberFilters } from "./usePlatformMemberFilters";
import "./styles.scss";

interface BotBuilderFormValues {
  allow_direct_messages: boolean;
  send_welcome_message: boolean;
  welcome_message: { body: string };
}

export const AudiencePage = ({
  agent,
  filters,
}: {
  agent: AgentProp;
  filters: Array<BaseFilter | ProfileFieldBaseFilter>;
}) => {
  const { isDarkAppearance } = useThemeContext();
  const { currentCommunitySettings } = usePunditUserContext();
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const lastFiltersRef = useRef<string>("");

  const { getValues, watch } = useFormContext<BotBuilderFormValues>();
  const shouldSendWelcomeMessage = getValues("send_welcome_message");
  const welcomeMessage = getValues("welcome_message");
  const isAllowDirectMessages = watch("allow_direct_messages");
  const shouldShowChatPreview = !useSmOrMdOrLgScreenMediaQuery();

  const isNewAudienceTabEnabled = Boolean(
    currentCommunitySettings?.ai_automations_enabled,
  );

  useEffect(() => {
    const filtersKey = JSON.stringify(filters);

    if (lastFiltersRef.current === filtersKey) {
      return;
    }

    lastFiltersRef.current = filtersKey;
    setIsLoading(true);

    void reactQueryPost(internalApi.communityBot.agents.audienceCount(), {
      members_filters: filters,
    })
      .then((res: { total_count: number }) => {
        if (lastFiltersRef.current === filtersKey) {
          setAudienceCount(res.total_count);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (lastFiltersRef.current === filtersKey) {
          setIsLoading(false);
        }
      });
  }, [filters]);

  const { finalFilters, isLoadingProfileFields, customProfileFields } =
    usePlatformMemberFilters();

  const onClose = () => {
    window.location.href = "/settings/ai-agents/agents";
  };

  if (!agent || isLoadingProfileFields) return null;

  if (isNewAudienceTabEnabled) {
    return (
      <div className="audience-page flex flex-col">
        <Header agent={agent} onClose={onClose} />
        <div
          className="flex h-full"
          style={{
            backgroundImage: `url(${isDarkAppearance ? botBuilderCanvasDark : botBuilderCanvas})`,
          }}
        >
          <CollapsedSidebar />
          <div className="bg-primary border-r-primary h-[calc(100vh-67px)] w-[586px] flex-col overflow-y-auto border-r">
            <div className="border-primary border-b px-6 py-5">
              <Typography.LabelLg weight="semibold">
                {t("community_bot.bot_builder.audience_page.title")}
              </Typography.LabelLg>
            </div>
            <TouchpointsSection />
            {isAllowDirectMessages && (
              <div className="border-primary h-auto border-y px-6 py-5">
                <div>
                  <div className="mb-4">
                    <Typography.LabelMd weight="semibold">
                      {t(
                        "community_bot.bot_builder.audience_page.targeting_title",
                      )}
                    </Typography.LabelMd>
                  </div>
                  <div className="mb-3">
                    <Typography.LabelSm color="text-light">
                      {t(
                        "community_bot.bot_builder.audience_page.targeting_description",
                      )}
                    </Typography.LabelSm>
                  </div>
                  <div className="border-primary border-collapse rounded-t-xl border bg-white px-6 py-3">
                    <Icon type="16-audience" size={16} className="mr-2" />
                    <Typography.LabelSm weight="semibold" color="text-light">
                      {t(
                        "community_bot.bot_builder.audience_page.audience_label_new",
                      )}
                    </Typography.LabelSm>
                    <span className="ml-1">
                      <Typography.LabelSm weight="semibold" color="text-dark">
                        {isLoading || audienceCount === null
                          ? "-"
                          : audienceCount}
                      </Typography.LabelSm>
                    </span>
                  </div>
                  <div className="border-primary border-collapse rounded-b-xl border-x border-b">
                    <DynamicFilters
                      stateFilters={filters}
                      platformFilters={finalFilters}
                      profileFieldFilters={customProfileFields}
                      showBackground={false}
                    >
                      <DynamicFilters.Panel>
                        <AdminAreaFiltersList />
                      </DynamicFilters.Panel>
                    </DynamicFilters>
                  </div>
                </div>
              </div>
            )}
          </div>
          {shouldShowChatPreview && <ChatPreview />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <Header agent={agent} onClose={onClose} />
      <div
        className="flex h-full"
        style={{
          backgroundImage: `url(${isDarkAppearance ? botBuilderCanvasDark : botBuilderCanvas})`,
        }}
      >
        <CollapsedSidebar />
        <div className="bg-primary border-r-primary w-[453px] flex-col border-r">
          <div className="border-primary border-b px-6 py-5">
            <Typography.LabelLg weight="semibold">
              {t("community_bot.bot_builder.audience_page.title")}
            </Typography.LabelLg>
          </div>
          <div className="h-auto px-6">
            <Form.Label
              fieldId="members_filters"
              label={t("community_bot.bot_builder.audience_page.filters_label")}
              hideBorder
              labelClassName="cursor-text text-sm"
            />
            <div className="bg-secondary border-primary border-collapse rounded-t-lg border px-6 py-3">
              <Icon type="16-audience" size={16} className="mr-2" />
              <Typography.LabelSm weight="semibold" color="text-light">
                {t("community_bot.bot_builder.audience_page.audience_label")}
              </Typography.LabelSm>
              <span className="ml-1">
                <Typography.LabelSm weight="semibold" color="text-dark">
                  {isLoading || audienceCount === null ? "-" : audienceCount}
                </Typography.LabelSm>
              </span>
            </div>
            <div className="border-primary border-collapse rounded-b-lg border-x border-b">
              <DynamicFilters
                stateFilters={filters}
                platformFilters={finalFilters}
                profileFieldFilters={customProfileFields}
              >
                <DynamicFilters.Panel>
                  <AdminAreaFiltersList />
                </DynamicFilters.Panel>
              </DynamicFilters>
            </div>
          </div>
          <div className="my-2 flex w-full items-center justify-center">
            <Icon type="audience-dots" />
          </div>
          <div className="h-auto px-6">
            <div className="border-primary rounded border px-6 pb-3">
              <Form.Checkbox
                className="flex w-auto font-normal"
                tooltipClassName="mt-[2px]"
                name="send_welcome_message"
                description={t(
                  "community_bot.bot_builder.audience_page.welcome_message_tooltip",
                )}
                isDescriptionTooltip
                label={t(
                  "community_bot.bot_builder.audience_page.send_welcome_message",
                )}
              />

              {shouldSendWelcomeMessage && (
                <TiptapEditor
                  className="mt-2"
                  editorClassName="mt-2 bg-secondary p-3 text-sm border-t-md rounded-t-md rounded-br-md"
                  disabled
                  editable={false}
                  value={welcomeMessage?.body}
                  showMenuBar={false}
                />
              )}
            </div>
          </div>
        </div>
        {shouldShowChatPreview && <ChatPreview />}
      </div>
    </div>
  );
};
